/**
 * Dorm layout helpers shared by the setup wizard and Choose New Layout: the
 * bunk rule, and the room's floor polygon for the layout previews.
 *
 * Pure so they import with no environment: anything reaching `../firebase`
 * calls `getAuth()` at MODULE SCOPE and throws `auth/invalid-api-key` on any
 * machine without env — CI has none. Same rule as
 * `3d/geometry/floorGeometry.js` and `materialRegions.js`.
 */

/**
 * Must these occupants share a bunk?
 *
 * A TRIPLE IS ONLY REPRESENTABLE AS A BUNK PLUS ONE MORE BED.
 * `TRIPLE_BED_CONFIGURATIONS` is the only arrangement table with room for a
 * third occupant and every entry in it is a bunk plus one bed; three separate
 * beds falls through to the TWO-bed table, where P3 appears in no config, so
 * their bed is dropped before placement and is not even reported unplaced.
 * Measured in a 30x30 room: two of three beds, so it is not a fit problem.
 * Missing table tracked as #2386.
 *
 * DERIVED at every point of use rather than written into state — forcing a
 * `bunkMode` flag true left a double silently bunked after 3 -> 2.
 */
export function bunkRequiredFor(occupants) {
  return occupants >= 3;
}

/** Whether a bunk is in force: the student's answer, or the room's demand. */
export function bunkActiveFor(occupants, bunkMode) {
  return Boolean(bunkMode) || bunkRequiredFor(occupants);
}

/**
 * The room's floor outline as `{x, z}` points, in the same world frame as the
 * walls and the returned layout items.
 *
 * Deliberately mirrors `prepareRoomData` in
 * shared-lib/dorm-optimizer-v2/pipeline.js — `floors[0].polygon`, else the wall
 * start points. `polygonCorners` is NOT consulted here precisely because the
 * optimizer does not consult it: the preview has to outline the same floor the
 * placement ran against.
 */
export function roomFloorPolygon(room, wallEndpoints) {
  const polygon = room?.floors?.[0]?.polygon;
  if (Array.isArray(polygon) && polygon.length >= 3) {
    return polygon.map((p) => ({ x: p.x, z: p.z }));
  }
  if (wallEndpoints.length >= 3) {
    return wallEndpoints.map((w) => ({ x: w.start.x, z: w.start.z }));
  }
  return null;
}
